import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus, Pencil, ArrowLeft, AlertCircle } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { supabaseUnderLeaseService } from '../../../services/supabase/underleases';
import type { UnderLease } from '../../../types/underlease';
import UnderLeaseFormModal from '../../../components/UnderLeases/UnderLeaseFormModal';
import { formatDate } from '../../../utils/dateUtils';

type SortField = 'underlease_lease' | 'underlease_holder' | 'underlease_start' | 'underlease_end' | 'underlease_use';

export default function UnderLeasesAdmin() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('underlease_lease');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedLease, setSelectedLease] = useState<UnderLease | undefined>(undefined);

  const { data: underleases, isLoading, error } = useQuery({
    queryKey: ['admin', 'underleases'],
    queryFn: () => supabaseUnderLeaseService.getAll(),
    retry: 1
  });

  const filteredUnderleases = underleases?.filter(lease => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      lease.underlease_lease.toString().includes(searchLower) ||
      lease.underlease_holder.toString().includes(searchLower) ||
      lease.underlease_use.toLowerCase().includes(searchLower)
    );
  });

  const sortedUnderleases = React.useMemo(() => {
    if (!filteredUnderleases) return [];
    
    return [...filteredUnderleases].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'underlease_lease':
          return direction * (a.underlease_lease - b.underlease_lease);
        case 'underlease_holder':
          return direction * (a.underlease_holder - b.underlease_holder);
        case 'underlease_start':
          return direction * a.underlease_start.localeCompare(b.underlease_start);
        case 'underlease_end':
          return direction * a.underlease_end.localeCompare(b.underlease_end);
        case 'underlease_use':
          return direction * a.underlease_use.localeCompare(b.underlease_use);
        default:
          return 0;
      }
    });
  }, [filteredUnderleases, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Under-Leases</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage property under-leases
            </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Under-Lease
          </button>
        </div>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search under-leases..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        />
        <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      {error ? (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Error loading under-lease data</p>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {error instanceof Error ? error.message : 'Please try again later'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <DualScrollTable>
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('underlease_lease')} className="flex items-center gap-1">
                    Lease ID {getSortIcon('underlease_lease')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('underlease_holder')} className="flex items-center gap-1">
                    Holder ID {getSortIcon('underlease_holder')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('underlease_start')} className="flex items-center gap-1">
                    Start Date {getSortIcon('underlease_start')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('underlease_end')} className="flex items-center gap-1">
                    End Date {getSortIcon('underlease_end')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('underlease_use')} className="flex items-center gap-1">
                    Use Type {getSortIcon('underlease_use')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedUnderleases.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="text-center py-6">
                      <h3 className="text-lg font-medium text-gray-900">No Under-Leases Found</h3>
                      <p className="mt-2 text-sm text-gray-500">Get started by adding your first under-lease.</p>
                      <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Add Under-Lease
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedUnderleases.map((lease) => (
                  <tr key={`${lease.underlease_lease}-${lease.underlease_holder}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedLease(lease);
                          setIsAddModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {lease.underlease_lease}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lease.underlease_holder}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(lease.underlease_start)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(lease.underlease_end)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {lease.underlease_use}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </DualScrollTable>
        </div>
      )}

      {isAddModalOpen && (
        <UnderLeaseFormModal
          lease={selectedLease}
          onClose={() => {
            setIsAddModalOpen(false);
            setSelectedLease(undefined);
          }}
        />
      )}
    </div>
  );
}
